import { memo } from 'react';
import {
  Handle,
  Position,
  useStore,
  type Node,
  type NodeProps,
} from '@xyflow/react';
import type { SubjectNodeData, SubjectRole } from '../subject-graph-layout';
import { nodeMeta, nodeTitle, zoomBand } from '../subject-graph-view';

// Forma por papel: de longe é o que diz o papel, já que a cor é da raiz.
const ROLE_SHAPE: Record<SubjectRole, string> = {
  root: 'rounded-lg',
  branch: 'rounded-lg',
  leaf: 'rounded-full',
  isolated: 'rounded-none',
};

/**
 * Nó do grafo de assuntos, com nível de detalhe por zoom: de longe é um bloco
 * na cor da raiz, no meio o nome, de perto o nome inteiro com papel, raiz e
 * contagens. A caixa tem tamanho fixo (vem do layout) — a faixa só troca o
 * conteúdo, ou o mapa pularia ao dar zoom.
 *
 * O papel não fica só na cor: raiz tem borda grossa e é mais alta, folha é
 * pílula, solto tem cantos retos e mora num bloco separado, e de perto o papel
 * vem escrito, junto com a incidência geral. De longe o bloco ganha contorno na
 * cor do texto, porque nem toda cor da paleta contrasta 3:1 com o fundo — o
 * contorno garante a forma.
 * `memo` + seletor de faixa: com mil nós, nenhum re-renderiza por tick de roda.
 */
export const SubjectNode = memo(function SubjectNode({
  data,
}: NodeProps<Node<SubjectNodeData>>) {
  const band = useStore((s) => zoomBand(s.transform[2]));
  const color = data.color ?? 'var(--color-border)';
  const far = band === 'far';

  const box = [
    'relative flex h-full w-full flex-col justify-center overflow-hidden px-3 pt-3 pb-2 text-center shadow-sm transition-opacity',
    ROLE_SHAPE[data.role],
    data.role === 'root' ? 'border-4' : 'border',
    data.highlighted
      ? 'ring-2 ring-(--color-accent) ring-offset-2 ring-offset-(--color-bg-primary)'
      : '',
    data.dimmed ? 'opacity-30' : '',
    far
      ? data.role === 'root'
        ? // Destacada, o contorno se afasta: no lugar dele cobriria o anel.
          `outline-4 outline-(--color-text-primary) ${data.highlighted ? 'outline-offset-4' : ''}`
        : 'outline outline-(--color-text-secondary)'
      : 'bg-(--color-bg-primary) text-(--color-text-primary)',
  ].join(' ');

  const meta = nodeMeta(data);

  return (
    <div
      className={box}
      style={{ borderColor: color, background: far ? color : undefined }}
      title={nodeTitle(data)}
    >
      {data.role !== 'root' && data.role !== 'isolated' && (
        <Handle
          type="target"
          position={Position.Left}
          className="!bg-(--color-accent) !border-none"
        />
      )}
      {!far && (
        <span
          aria-hidden
          className="absolute inset-x-0 top-0 h-1.5"
          style={{ background: color }}
        />
      )}
      {band === 'mid' && (
        <p className="truncate text-xs font-medium">{data.label}</p>
      )}
      {band === 'near' && (
        <>
          <p className="line-clamp-2 text-xs font-medium leading-4">
            {data.label}
          </p>
          <p className="text-(--color-text-secondary) mt-1 truncate text-xs leading-4">
            {meta}
          </p>
        </>
      )}
      {(data.role === 'root' || data.role === 'branch') && (
        <Handle
          type="source"
          position={Position.Right}
          className="!bg-(--color-accent) !border-none"
        />
      )}
    </div>
  );
});
